import { ObservableModelNode } from "../observable";
import type { SpecsAutoFormFieldMetadataMap } from "../autoFormMetadata";
import {
  cloneValue,
  expectObject,
  readBoolean,
  readOptionalNumber,
  readString,
  splitKnown,
} from "../utils";

export class DecorationTechpackModel extends ObservableModelNode {
  static autoFormFields: SpecsAutoFormFieldMetadataMap = {
    decorationTypePrefix: {
      label: "Decoration type prefix",
      fieldType: "text",
    },
    decorationNameKey: { label: "Decoration name key", fieldType: "text" },
    putSizeInFilename: { label: "Put size in filename", fieldType: "boolean" },
    addBordersToAsset: {
      label: "Add borders to asset",
      fieldType: "boolean",
    },
    displayAlignmentMarks: {
      label: "Display alignment marks",
      fieldType: "boolean",
    },
    widthInches: {
      label: "Width inches",
      fieldType: "number",
      allowNull: true,
    },
    heightInches: {
      label: "Height inches",
      fieldType: "number",
      allowNull: true,
    },
    borderType: { label: "Border type", fieldType: "text" },
    overwriteFabric: {
      label: "Overwrite fabric",
      fieldType: "json",
      minRows: 4,
    },
    extra: { label: "Extra JSON", fieldType: "json", minRows: 4 },
  };

  decorationTypePrefix!: string;
  decorationNameKey!: string;
  putSizeInFilename!: boolean;
  addBordersToAsset!: boolean;
  displayAlignmentMarks!: boolean;
  hasPutSizeInFilename!: boolean;
  hasAddBordersToAsset!: boolean;
  hasDisplayAlignmentMarks!: boolean;
  widthInches!: number | null;
  heightInches!: number | null;
  borderType!: string;
  overwriteFabric!: Record<string, unknown>;
  extra!: Record<string, unknown>;

  constructor() {
    super();
    this.defineObservableValue("decorationTypePrefix", "draft");
    this.defineObservableValue("decorationNameKey", "draft");
    this.defineObservableValue("putSizeInFilename", false);
    this.defineObservableValue("addBordersToAsset", false);
    this.defineObservableValue("displayAlignmentMarks", false);
    this.defineObservableValue("hasPutSizeInFilename", true);
    this.defineObservableValue("hasAddBordersToAsset", true);
    this.defineObservableValue("hasDisplayAlignmentMarks", true);
    this.defineObservableValue("widthInches", null);
    this.defineObservableValue("heightInches", null);
    this.defineObservableValue("borderType", "");
    this.defineObservableValue("overwriteFabric", {});
    this.defineObservableValue("extra", {});
  }

  restore(snapshot: unknown) {
    const source = expectObject(snapshot, "decoration techpack");
    this.decorationTypePrefix = readString(source, "decorationTypePrefix");
    this.decorationNameKey = readString(source, "decorationNameKey");
    this.hasPutSizeInFilename = "putSizeInFilename" in source;
    this.hasAddBordersToAsset = "addBordersToAsset" in source;
    this.hasDisplayAlignmentMarks = "displayAlignmentMarks" in source;
    this.putSizeInFilename = readBoolean(source, "putSizeInFilename", false);
    this.addBordersToAsset = readBoolean(source, "addBordersToAsset", false);
    this.displayAlignmentMarks = readBoolean(
      source,
      "displayAlignmentMarks",
      false
    );
    this.widthInches = readOptionalNumber(source, "widthInches");
    this.heightInches = readOptionalNumber(source, "heightInches");
    this.borderType = readString(source, "borderType");
    this.overwriteFabric =
      source.overwriteFabric &&
      typeof source.overwriteFabric === "object" &&
      !Array.isArray(source.overwriteFabric)
        ? cloneValue(source.overwriteFabric as Record<string, unknown>)
        : {};
    this.extra = splitKnown(source, [
      "decorationTypePrefix",
      "decorationNameKey",
      "putSizeInFilename",
      "addBordersToAsset",
      "displayAlignmentMarks",
      "widthInches",
      "heightInches",
      "borderType",
      "overwriteFabric",
    ]);
    return this;
  }

  save() {
    return {
      ...cloneValue(this.extra),
      ...(this.decorationTypePrefix
        ? { decorationTypePrefix: this.decorationTypePrefix }
        : {}),
      ...(this.decorationNameKey
        ? { decorationNameKey: this.decorationNameKey }
        : {}),
      ...(this.hasPutSizeInFilename || this.putSizeInFilename
        ? { putSizeInFilename: this.putSizeInFilename }
        : {}),
      ...(this.hasAddBordersToAsset || this.addBordersToAsset
        ? { addBordersToAsset: this.addBordersToAsset }
        : {}),
      ...(this.hasDisplayAlignmentMarks || this.displayAlignmentMarks
        ? { displayAlignmentMarks: this.displayAlignmentMarks }
        : {}),
      ...(this.widthInches !== null ? { widthInches: this.widthInches } : {}),
      ...(this.heightInches !== null
        ? { heightInches: this.heightInches }
        : {}),
      ...(this.borderType ? { borderType: this.borderType } : {}),
      ...(Object.keys(this.overwriteFabric).length > 0
        ? { overwriteFabric: cloneValue(this.overwriteFabric) }
        : {}),
    };
  }
}
